import { Game } from '@optools/services/operator-gateway/types/generated/graphql';
import { Team } from '@optools/services/statsapi/types';

export function filterGamesByTerm<TGame extends Pick<Game, 'gamePk' | 'home' | 'away' | 'venue'>>(
  schedule: TGame[],
  filter: string,
) {
  if (!filter) return schedule;

  const term = filter.toLowerCase();

  return schedule.filter((game) => {
    const { away, gamePk, home, venue } = game;
    const keywords = [
      gamePk.toString(),
      away.abbreviation,
      away.name,
      away.shortName,
      home.abbreviation,
      home.name,
      home.shortName,
      venue.name,
    ];
    return keywords.some((value) => value.toLowerCase().includes(term));
  });
}

/**
 * Finds a team by ID, treating negative and positive IDs as equivalent.
 * For example, both -123 and 123 will match team with id 123.
 */
export function findTeamById(teams: Team[], teamId: number): Team | undefined {
  const absoluteId = Math.abs(teamId);
  return teams.find((team) => Math.abs(team.id) === absoluteId);
}

/**
 * Filters entities by team properties and additional fields.
 * Useful when you want to search by both team info and entity-specific properties.
 */
export function filterTeamsBySearchTerm<
  TEntity extends { team?: Team; teamId?: number | null | undefined },
>(
  entities: TEntity[],
  filter: string,
  additionalFields?: (entity: TEntity) => (string | number)[],
): TEntity[] {
  if (!filter) return entities;

  const term = filter.toLowerCase();

  return entities.filter((entity) => {
    const keywords: string[] = [];

    if (entity.team) {
      const { abbreviation, id, name, teamName, venue } = entity.team;
      keywords.push(name, abbreviation, teamName, venue.name, id.toString());
    }

    if (entity.teamId) {
      keywords.push(entity.teamId.toString());
    }

    if (additionalFields) {
      const extraFields = additionalFields(entity);
      keywords.push(...extraFields.map((field) => field.toString()));
    }

    return keywords.some((value) => value.toLowerCase().includes(term));
  });
}
